import React from 'react';
import { useTranslation } from 'react-i18next';
import { LabelInput } from '@/components/ui/inputs/LabelInput';
import { Selector } from '@/components/ui/inputs/selector';
import { PrimaryButton } from '@/components/ui/buttons/PrimaryButton';
import { IconScout } from '@/components/ui/images/IconScout';
import {
  useRegisterAttendanceForm,
  type AttendanceRecord,
} from '@/hooks/attendance/myAttendance/useRegisterAttendanceForm';
import { GoogleStaticMap } from '@/components/ui/maps/GoogleStaticMap';
import { PermissionGuard, SidePanelFormLayout } from '@/components/layout';
import type { RegisterAttendanceFormValues } from '@/schemas/attendance/registerAttendanceForm';


interface RegisterAttendanceFormProps {
  onSubmit?: (data: RegisterAttendanceFormValues) => void;
  readOnly?: boolean;
  record?: AttendanceRecord;
}

export const RegisterAttendanceForm: React.FC<RegisterAttendanceFormProps> = ({
  onSubmit,
  readOnly,
  record,
}) => {
  const { t: translate } = useTranslation();
  const {
    fecha,
    hora,
    locations,
    locationValue,
    effectiveReadOnly,
    setLocationValue,
    isSubmitting,
    handleSubmit,
    mapUrl,
    mapLoading,
    mapUnavailable,
    errors,
    isSubmitted,
  } = useRegisterAttendanceForm({ onSubmit, readOnly, record });

  return (
    <SidePanelFormLayout
      onSubmit={handleSubmit}
      data-testid="registro-asistencia-form"
    >
      {/* Static map preview */}
      <div className="w-full flex justify-center">
        <GoogleStaticMap
          width={320}
          height={220}
          mapUrl={mapUrl}
          loading={mapLoading}
          isUnavailable={mapUnavailable}
        />
      </div>

      <SidePanelFormLayout.Fields>
        <LabelInput
          label={translate('attendance:fields.date')}
          value={fecha}
          disabled
          iconRight={<IconScout name="calendarAlt" size="sm" />}
        />
        <LabelInput
          label={translate('attendance:incident.form.time')}
          value={hora}
          disabled
          iconRight={<IconScout name="clockThree" size="sm" />}
        />
        <Selector
          label={translate('attendance:manual.fields.location')}
          placeholder={translate('attendance:incident.form.locationPlaceholder')}
          options={locations}
          value={locationValue}
          onChange={(val: string | string[]) => {
            if (Array.isArray(val)) {
              const first = val[0] ?? '';
              setLocationValue(first);
            } else {
              setLocationValue(val);
            }
          }}
          disabled={effectiveReadOnly || isSubmitting}
          color={errors?.ubicacion && isSubmitted ? 'error' : 'default'}
          supportingText={
            errors?.ubicacion && isSubmitted
              ? translate(errors.ubicacion.message as string)
              : undefined
          }
          required
        />
      </SidePanelFormLayout.Fields>

      {!effectiveReadOnly && (
        <SidePanelFormLayout.Footer>
          <PermissionGuard action="create">
            <PrimaryButton type="submit" fullWidth isLoading={isSubmitting} disabled={isSubmitting}>
              {translate('attendance:create')}
            </PrimaryButton>
          </PermissionGuard>
        </SidePanelFormLayout.Footer>
      )}
    </SidePanelFormLayout>
  );
};

export default RegisterAttendanceForm;
